// assets/eos-danisman.js
import { Component } from '@theme/component';

/*
 * EOS parça rehberi: a rule-based, step-by-step product finder presented as a messaging thread.
 * It is an automated assistant and is never presented as a person: the account is the brand, the
 * avatar is the brand mark, the status line says the answers are automatic, and the "preparing"
 * indicator is three dots whose screen-reader text makes no claim that anyone is typing.
 *
 * Questions and labels come from the section settings (sections/eos-danisman.liquid); the options at
 * each step are derived from the live catalogue, fetched once on first open from
 * sections/eos-danisman-veri.liquid through the Section Rendering API.
 *
 * Adding a step = adding an object to ADIMLAR; the rendering code does not change. Unless `herZaman`
 * is set, a step is skipped when the remaining products offer no option, and silently applied when
 * its only option would keep every remaining product.
 *
 * Two contracts reach outside this file:
 *   - `data-eos-danisman="hazir"` on <html> while the component is connected. blocks/eos-danisman-serit.liquid
 *     shows its in-page entry point only under that flag, so a disabled guide leaves no dead button.
 *   - the "connect me to a person" action reads the footer's WhatsApp link (sections/eos-footer.liquid,
 *     contact block setting `whatsapp`). Liquid cannot read another section's block settings, so the
 *     number is picked up from the rendered anchor; with no number the action stays hidden.
 */

const VERI_BOLUMU = 'eos-danisman-veri';
const KAPALI_ANAHTARI = 'eos-danisman-kapatildi';
const EN_FAZLA_SONUC = 6;
/** Typing-indicator window, in ms. Long enough to read as a reply, short enough not to be a wait. */
const GECIKME_ALT = 250;
const GECIKME_UST = 600;
/**
 * Where the shop's WhatsApp number is read from, most specific first. Scoped to the footer on
 * purpose: the number is the footer contact block's `whatsapp` setting, and an empty setting must
 * mean no button — not a number borrowed from some other section that happens to link to wa.me.
 */
const WHATSAPP_SECICILER = ['.eos-footer a[href*="wa.me/"]', 'footer a[href*="wa.me/"]'];
/**
 * Personalisation answers, in display order. One answer may cover several `eos.kisisellestirme`
 * values: "Burç" returns single- and two-sign pieces. The label comes from the group's first value,
 * or from the only value present (so a catalogue with just two-sign pieces is offered "İki burç").
 */
const TIP_GRUPLARI = [['tek-harf'], ['iki-harf'], ['tek-isim'], ['iki-isim'], ['zodyak', 'farkli-zodyak']];

/**
 * @typedef {{ name: string, key: string }} Renk
 * @typedef {{ id: number, colour: string, first: boolean }} Varyant
 * @typedef {{ handle: string, title: string, url: string, tags: string[], tip: string, colours: Renk[],
 *   variants: Varyant[], price_min: number, price_label: string, price_varies: boolean, image: string | null }} Urun
 * @typedef {{ urunler: Urun[], model: string, hediye: boolean, renk: string }} Baglam
 * @typedef {{ deger: string | null, etiket: string }} Secenek
 * @typedef {object} Adim
 * @property {string} id           Unique step name.
 * @property {string} soru         Key of the question text in the settings JSON.
 * @property {boolean} [farkEtmez] Adds a "Fark etmez" answer that leaves the context unchanged.
 * @property {boolean} [herZaman]  Asked even when the data offers no real choice.
 * @property {(baglam: Baglam, metin: any) => Secenek[]} secenekler Options built from the remaining products.
 * @property {(baglam: Baglam, deger: string) => Baglam} uygula    Returns a NEW context; never mutates.
 */

/** @param {Baglam} b @param {(u: Urun) => boolean} kosul */
const suz = (b, kosul) => ({ ...b, urunler: b.urunler.filter(kosul) });

/** @type {Adim['secenekler']} The model's allowed colours that remaining products carry, in settings order. */
function renkSecenekleri(b, metin) {
  const adlar = new Map(b.urunler.flatMap((u) => u.colours.map((r) => [r.key, r.name])).reverse());
  const sira = b.model ? metin.renkler?.[b.model] ?? [] : [...new Set(b.urunler.flatMap((u) => u.colours.map((r) => r.key)))];
  return sira.filter((/** @type {string} */ key) => adlar.has(key)).map((key) => ({ deger: key, etiket: adlar.get(key) }));
}

/** @type {Adim['secenekler']} Two or three ranges cut from the distinct minimum prices; none when all are equal. */
function butceSecenekleri(b) {
  const fiyatlar = [...new Set(b.urunler.map((u) => u.price_min))].sort((x, y) => x - y);
  if (fiyatlar.length < 2) return [];
  const etiket = (/** @type {number} */ f) => b.urunler.find((u) => u.price_min === f)?.price_label ?? '';
  const grup = Math.min(3, fiyatlar.length);
  return Array.from({ length: grup }, (_, i) => {
    const alt = fiyatlar[Math.floor((i * fiyatlar.length) / grup)];
    const ust = fiyatlar[Math.floor(((i + 1) * fiyatlar.length) / grup) - 1];
    return { deger: `${alt}-${ust}`, etiket: alt === ust ? etiket(alt) : `${etiket(alt)} – ${etiket(ust)}` };
  });
}

/** @type {Adim[]} */
/* Kadın/erkek adımı kaldırıldı (2026-09-29): mağaza cinsiyet ayrımı yapmıyor,
   her renk her müşteriye açık. `model` boş kaldığı için renk adımı ürünlerin
   taşıdığı bütün renkleri sunar. */
export const ADIMLAR = [
  {
    id: 'amac',
    soru: 'soru_amac',
    herZaman: true,
    secenekler: (_, metin) => [
      { deger: 'kendim', etiket: metin.cevap_kendim },
      { deger: 'hediye', etiket: metin.cevap_hediye },
    ],
    uygula: (b, deger) => ({ ...b, hediye: deger === 'hediye' }),
  },
  {
    id: 'kategori',
    soru: 'soru_kategori',
    farkEtmez: true,
    secenekler: (b, metin) =>
      metin.kategoriListesi.filter((/** @type {Secenek} */ t) => b.urunler.some((u) => u.tags.includes(String(t.deger)))),
    uygula: (b, etiket) => suz(b, (u) => u.tags.includes(etiket)),
  },
  {
    id: 'tip',
    soru: 'soru_tip',
    farkEtmez: true,
    secenekler: (b, metin) =>
      TIP_GRUPLARI.map((grup) => ({ grup, mevcut: grup.filter((tip) => b.urunler.some((u) => u.tip === tip)) }))
        .filter(({ mevcut }) => mevcut.length > 0)
        .map(({ grup, mevcut }) => ({
          deger: mevcut.join('+'),
          etiket: metin.tipler?.[mevcut.length > 1 ? grup[0] : mevcut[0]],
        })),
    uygula: (b, deger) => suz(b, (u) => deger.split('+').includes(u.tip)),
  },
  {
    id: 'renk',
    soru: 'soru_renk',
    farkEtmez: true,
    secenekler: renkSecenekleri,
    uygula: (b, renk) => ({ ...suz(b, (u) => u.colours.some((r) => r.key === renk)), renk }),
  },
  {
    id: 'butce',
    soru: 'soru_butce',
    farkEtmez: true,
    secenekler: butceSecenekleri,
    uygula: (b, aralik) => {
      const [alt, ust] = aralik.split('-').map(Number);
      return suz(b, (u) => u.price_min >= alt && u.price_min <= ust);
    },
  },
];

/**
 * What a step does with the current products: ask, or skip (applying its only option).
 * @param {Adim} adim @param {Baglam} b @param {any} metin
 * @returns {{ sor: Secenek[] } | { otomatik: string | null }}
 */
export function adimCoz(adim, b, metin) {
  const secenekler = adim.secenekler(b, metin).map((s) => ({ ...s, etiket: s.etiket || String(s.deger) }));
  if (adim.herZaman) return { sor: secenekler };
  if (secenekler.length === 0) return { otomatik: null };
  const tek = secenekler.length === 1 ? secenekler[0].deger : null;
  if (tek !== null && adim.uygula(b, tek).urunler.length === b.urunler.length) return { otomatik: tek };
  return { sor: adim.farkEtmez ? [...secenekler, { deger: null, etiket: metin.cevap_fark_etmez }] : secenekler };
}

/**
 * Opens the product on the chosen model and, when a colour was chosen, on that colour's variant
 * whose other options (e.g. chain length) are the first values.
 * @param {Urun} urun @param {Baglam} b
 */
export function urunAdresi(urun, b) {
  const adres = new URL(urun.url, window.location.origin);
  if (b.model) adres.searchParams.set('model', b.model);
  const adaylar = b.renk ? urun.variants.filter((v) => v.colour === b.renk) : [];
  const varyant = adaylar.find((v) => v.first) ?? adaylar[0];
  if (varyant) adres.searchParams.set('variant', String(varyant.id));
  return `${adres.pathname}${adres.search}`;
}

/** @returns {Promise<Urun[]>} */
async function veriYukle() {
  const kok = window.Shopify?.routes?.root || '/';
  const yanit = await fetch(`${kok.endsWith('/') ? kok : `${kok}/`}?section_id=${VERI_BOLUMU}`);
  if (!yanit.ok) throw new Error(`HTTP ${yanit.status}`);
  const belge = new DOMParser().parseFromString(await yanit.text(), 'text/html');
  const veri = JSON.parse(belge.getElementById('EosDanismanVeri')?.textContent ?? 'null');
  if (!Array.isArray(veri?.products)) throw new Error('Beklenmeyen veri');
  return veri.products.map((/** @type {Urun} */ u) => ({
    ...u,
    tags: (u.tags ?? []).map((etiket) => String(etiket).toLowerCase()),
    colours: u.colours ?? [],
    variants: u.variants ?? [],
  }));
}

/** Reads (and optionally writes) the "closed this session" flag; blocked storage just remembers nothing. */
function oturum(/** @type {string} */ yaz = '') {
  try {
    if (yaz) window.sessionStorage.setItem(KAPALI_ANAHTARI, yaz);
    return window.sessionStorage.getItem(KAPALI_ANAHTARI);
  } catch {
    return null;
  }
}

/** @param {string} etiket @param {string} sinif @param {string} [metin] */
function olustur(etiket, sinif, metin) {
  const el = document.createElement(etiket);
  if (sinif) el.className = sinif;
  if (metin !== undefined) el.textContent = metin;
  return el;
}

/** Local clock, short form. An unusable document language just leaves the stamp empty. */
function damga() {
  try {
    return new Date().toLocaleTimeString(document.documentElement.lang || undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

const azHareket = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

/**
 * Custom analytics event. Fire-and-forget: a missing or failing API never stops the guide.
 * @param {string} ad @param {Record<string, unknown>} veri
 */
function analitikYayinla(ad, veri) {
  try {
    window.Shopify?.analytics?.publish?.(ad, veri);
  } catch {
    /* Analytics is optional; the guide never depends on it. */
  }
}

/** The shop's WhatsApp number as rendered in the footer, or null when the setting is empty. */
function whatsappNumarasi() {
  for (const secici of WHATSAPP_SECICILER) {
    for (const baglanti of document.querySelectorAll(secici)) {
      const numara = baglanti.getAttribute('href')?.match(/wa\.me\/(\d{6,})/)?.[1];
      if (numara) return numara;
    }
  }
  return null;
}

/**
 * @typedef {object} Refs
 * @property {HTMLButtonElement} baslat
 * @property {HTMLElement} panel
 * @property {HTMLElement} log
 * @property {HTMLElement} cevaplar
 * @property {HTMLButtonElement} geri
 * @property {HTMLButtonElement} bastan
 * @property {HTMLAnchorElement} [canli]
 * @property {HTMLElement} [girisSaat]
 * @property {HTMLScriptElement} ayarlar
 * @extends {Component<Refs>}
 */
class EosDanisman extends Component {
  /** @type {Urun[] | null} */
  #urunler = null;
  /** @type {{ adim: number, baglam: Baglam, isaret: number }[]} One entry per question shown, with its context. */
  #gecmis = [];
  /** @type {Secenek[]} */
  #secenekler = [];
  #sonucta = false;
  /** 'adim' = tam ekran adım akışı (varsayılan), 'sohbet' = eski balonlu görünüm. */
  get #adimBicimi() { return this.dataset.bicim !== 'sohbet'; }
  #yukleniyor = false;
  /** True while the indicator is up: answers are gone from the DOM, back/restart are held. */
  #bekliyor = false;
  /** Bumped by every #ilerle; a pending delay whose token is stale resolves into nothing. */
  #jeton = 0;
  /** The control that opened the panel — the launcher, or an in-page strip button. */
  #acan = /** @type {HTMLElement | null} */ (null);
  /** @type {any} */
  #metinler = null;

  connectedCallback() {
    super.connectedCallback();
    if (oturum()) this.classList.add('eos-danisman--sade');
    // Contract with blocks/eos-danisman-serit.liquid: its in-page entry point only shows under this flag.
    document.documentElement.dataset.eosDanisman = 'hazir';
    if (window.Shopify?.designMode) document.addEventListener('shopify:section:select', this.#editorSecimi);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    // Only the last instance clears the flag, so a re-render that connects before it disconnects
    // does not blink the in-page strip out of existence.
    if (!document.querySelector('eos-danisman-component')) delete document.documentElement.dataset.eosDanisman;
    document.removeEventListener('shopify:section:select', this.#editorSecimi);
  }

  /** In the theme editor, selecting the section opens the panel for review. */
  #editorSecimi = (/** @type {Event} */ event) => {
    if (event.target instanceof Node && event.target.contains(this)) this.#goster(true);
  };

  get #metin() {
    if (this.#metinler) return this.#metinler;
    const ham = JSON.parse(this.refs.ayarlar?.textContent || '{}');
    const kategoriListesi = String(ham.kategoriler ?? '')
      .split(',')
      .map((parca) => {
        const [etiket = '', ...ad] = parca.split(':');
        return { deger: etiket.trim().toLowerCase(), etiket: ad.join(':').trim() };
      })
      .filter((tur) => tur.deger);
    this.#metinler = { ...ham, kategoriListesi };
    return this.#metinler;
  }

  degistir() {
    this.#acan = null;
    this.#goster(this.refs.panel.hidden);
  }

  /**
   * Opens the panel from anywhere on the page — used by the in-page strip block, which must act on
   * this component rather than repeat the flow. Focus returns to whichever control opened it.
   * @param {Event} [event]
   */
  ac(event) {
    const kaynak = event?.target;
    this.#acan = kaynak instanceof HTMLElement && kaynak !== this.refs.baslat ? kaynak : null;
    if (this.refs.panel.hidden) this.#goster(true);
    else this.refs.panel.focus({ preventScroll: true });
  }

  kapat() {
    this.#goster(false);
  }

  /** @param {KeyboardEvent} event */
  tus(event) {
    if (event.key === 'Escape' && !this.refs.panel.hidden) this.#goster(false);
  }

  /** @param {Event} event */
  cevap(event) {
    if (this.#bekliyor) return;
    const secim = this.#secenekler[Number(/** @type {HTMLElement} */ (event.target).dataset.sira)];
    const son = this.#gecmis.at(-1);
    if (!secim || !son || this.#sonucta) return;
    if (!this.#adimBicimi) this.#balon(secim.etiket, 'musteri');
    const adim = ADIMLAR[son.adim];
    this.#ilerle(son.adim + 1, secim.deger === null ? son.baglam : adim.uygula(son.baglam, secim.deger));
  }

  geri() {
    if (this.#bekliyor) return;
    // While a question is open its own entry is the last one: step past it.
    if (this.#gecmis.length < (this.#sonucta ? 1 : 2)) return;
    if (!this.#sonucta) this.#gecmis.pop();
    const onceki = this.#gecmis.pop();
    if (!onceki) return;
    this.#sonucta = false;
    this.#kes(onceki.isaret);
    this.#ilerle(onceki.adim, onceki.baglam);
  }

  bastan() {
    if (this.#bekliyor || !this.#urunler) return;
    this.#gecmis = [];
    this.#sonucta = false;
    this.#kes(0);
    this.#ilerle(0, { urunler: this.#urunler, model: '', hediye: false, renk: '' });
  }

  /** @param {boolean} acik */
  #goster(acik) {
    const { panel, baslat } = this.refs;
    if (panel.hidden !== acik) return;
    panel.hidden = !acik;
    this.toggleAttribute('data-acik', acik);
    baslat.setAttribute('aria-expanded', String(acik));
    if (!acik) {
      oturum('1');
      // The launcher is fixed and always in view; an in-page strip may not be, so let it scroll back.
      const hedef = this.#acan?.isConnected ? this.#acan : baslat;
      this.#acan = null;
      hedef.focus({ preventScroll: hedef === baslat });
      return;
    }
    if (this.refs.girisSaat && !this.refs.girisSaat.textContent) this.refs.girisSaat.textContent = damga();
    this.#canliBaglantiKur();
    panel.focus({ preventScroll: true });
    // The editor opens the panel on section select; that is the merchant, not a shopper.
    if (!window.Shopify?.designMode) {
      analitikYayinla('eos_danisman_acildi', {
        kaynak: this.#acan ? 'sayfa-ici' : 'baslatici',
        sayfa: window.location.pathname,
      });
    }
    if (!this.#urunler && !this.#yukleniyor) this.#basla();
  }

  /**
   * Points the "connect me to a person" action at the shop's WhatsApp number. The number lives in a
   * footer block setting that Liquid cannot read from here, so it is taken from the rendered footer
   * link; with no number the action stays hidden rather than promising a channel that does not exist.
   */
  #canliBaglantiKur() {
    const dugme = this.refs.canli;
    if (!dugme) return;
    const numara = whatsappNumarasi();
    if (!numara) return;
    const adres = new URL(`https://wa.me/${numara}`);
    const mesaj = String(this.#metin.canli_mesaj ?? '').trim();
    if (mesaj) adres.searchParams.set('text', mesaj);
    dugme.href = adres.toString();
    dugme.hidden = false;
  }

  async #basla() {
    this.#yukleniyor = true;
    this.#kes(0);
    const bekleme = this.#balon(this.#metin.yukleniyor, 'rehber');
    try {
      this.#urunler = await veriYukle();
      bekleme.remove();
      this.bastan();
    } catch {
      bekleme.remove();
      const baglanti = olustur('a', '', this.#metin.tumu_etiket);
      baglanti.setAttribute('href', this.#metin.tumu_url || '/collections/all');
      this.#balon(this.#metin.hata, 'rehber').querySelector('.eos-danisman__metin')?.append(' ', baglanti);
    } finally {
      this.#yukleniyor = false;
    }
  }

  /**
   * Walks forward from step `sira`, skipping steps the data leaves no choice in, and lets the
   * indicator run before each new message.
   * @param {number} sira @param {Baglam} b
   */
  async #ilerle(sira, b) {
    const jeton = ++this.#jeton;
    this.#secenekler = [];
    this.refs.cevaplar.replaceChildren();
    /* ADIM BİÇİMİ: ekranda tek soru durur, sohbet birikmez. Sohbet biçiminde
       balonlar üst üste eklenirdi; müşteri aynı anda hem eski cevaplarını hem
       yeni soruyu okumak zorunda kalıyordu. Geri düğmesi zaten var, geçmiş
       oradan gezilir. */
    if (this.#adimBicimi) this.refs.log.replaceChildren();
    let guncel = b;
    for (let i = sira; i < ADIMLAR.length; i++) {
      const karar = adimCoz(ADIMLAR[i], guncel, this.#metin);
      if ('otomatik' in karar) {
        if (karar.otomatik !== null) guncel = ADIMLAR[i].uygula(guncel, karar.otomatik);
        continue;
      }
      const soru = this.#metin[ADIMLAR[i].soru] ?? '';
      await this.#bekle(soru);
      if (jeton !== this.#jeton) return;
      this.#gecmis.push({ adim: i, baglam: guncel, isaret: this.#sohbetUzunlugu() });
      this.#balon(soru, 'rehber');
      this.#cevaplariGoster(karar.sor, soru);
      return;
    }
    await this.#bekle(this.#metin[guncel.hediye ? 'sonuc_hediye' : 'sonuc_kendim']);
    if (jeton !== this.#jeton) return;
    this.#sonuclariGoster(guncel);
  }

  /**
   * Holds the three-dot indicator for a few hundred milliseconds so a reply reads as a reply.
   * Reduced motion skips both the indicator and the wait; nothing here depends on rAF.
   * @param {string} [metin]
   */
  #bekle(metin) {
    /* Adım biçiminde "yazıyor" göstergesi yok: bekleme, sohbet yanılsamasını
       kurmak içindi; adım akışında yalnız gecikme olur. */
    if (this.#adimBicimi || azHareket()) return Promise.resolve();
    const satir = this.#yaziyor();
    this.#bekliyor = true;
    this.#kontrolleriGuncelle();
    const sure = Math.min(GECIKME_UST, GECIKME_ALT + String(metin ?? '').length * 6);
    return new Promise((coz) => {
      window.setTimeout(() => {
        satir.remove();
        this.#bekliyor = false;
        this.#kontrolleriGuncelle();
        coz(undefined);
      }, sure);
    });
  }

  /** The dots carry no words; the readable label says an answer is being prepared, nothing more. */
  #yaziyor() {
    const satir = olustur('div', 'eos-danisman__satir eos-danisman__satir--rehber');
    satir.setAttribute('data-gecici', '');
    const balon = olustur('div', 'eos-danisman__balon eos-danisman__balon--yaziyor');
    balon.append(olustur('span', 'eos-danisman__gizli', this.#metin.yaziyor_etiket ?? ''));
    for (let i = 0; i < 3; i++) {
      const nokta = olustur('span', 'eos-danisman__nokta');
      nokta.setAttribute('aria-hidden', 'true');
      balon.append(nokta);
    }
    satir.append(balon);
    this.refs.log.append(satir);
    this.#enAlta();
    return satir;
  }

  /** @param {Secenek[]} secenekler @param {string} soru */
  #cevaplariGoster(secenekler, soru) {
    const { cevaplar } = this.refs;
    this.#secenekler = secenekler;
    cevaplar.setAttribute('aria-label', soru);
    cevaplar.replaceChildren(
      ...secenekler.map((secenek, i) => {
        const dugme = olustur('button', 'eos-danisman__secenek', secenek.etiket);
        dugme.setAttribute('type', 'button');
        dugme.setAttribute('on:click', '/cevap');
        dugme.dataset.sira = String(i);
        return dugme;
      })
    );
    this.#kontrolleriGuncelle();
    this.#odakla(/** @type {HTMLElement | null} */ (cevaplar.firstElementChild));
  }

  /** @param {Baglam} b */
  #sonuclariGoster(b) {
    this.#sonucta = true;
    this.#secenekler = [];
    this.refs.cevaplar.replaceChildren();
    const satir = olustur('div', 'eos-danisman__satir eos-danisman__satir--rehber eos-danisman__satir--sonuc');
    const balon = olustur('div', 'eos-danisman__balon');
    const liste = b.urunler.slice(0, EN_FAZLA_SONUC).map((urun) => this.#kart(urun, b));
    if (liste.length === 0) {
      balon.append(olustur('p', 'eos-danisman__metin', this.#metin.sonuc_bos));
    } else {
      const ul = olustur('ul', 'eos-danisman__liste');
      ul.append(...liste);
      balon.append(olustur('p', 'eos-danisman__sonuc-baslik', this.#metin[b.hediye ? 'sonuc_hediye' : 'sonuc_kendim']), ul);
    }
    balon.append(olustur('span', 'eos-danisman__saat', damga()));
    satir.append(balon);
    this.refs.log.append(satir);
    // The head of a long list matters more than its tail: show the heading, not the last card.
    this.refs.log.scrollTop = Math.max(0, satir.offsetTop - 12);
    this.#kontrolleriGuncelle();
    this.#odakla(satir.querySelector('a') ?? this.refs.bastan);
  }

  /** @param {Urun} urun @param {Baglam} b */
  #kart(urun, b) {
    const baglanti = olustur('a', 'eos-danisman__kart');
    baglanti.setAttribute('href', urunAdresi(urun, b));
    const gorsel = olustur('span', 'eos-danisman__gorsel');
    gorsel.setAttribute('aria-hidden', 'true');
    if (urun.image) {
      const img = Object.assign(document.createElement('img'), { src: urun.image, alt: '', width: 112, height: 112 });
      img.loading = 'lazy';
      gorsel.append(img);
    } else {
      // No photo yet: a quiet initial on ivory, never a stand-in product image.
      gorsel.textContent = urun.title.trim().charAt(0).toLocaleUpperCase('tr-TR') || 'E';
    }
    const kalip = String(this.#metin.fiyat_baslayan ?? '');
    const fiyat = urun.price_varies && kalip.includes('{fiyat}') ? kalip.replace('{fiyat}', urun.price_label) : urun.price_label;
    const bilgi = olustur('span', 'eos-danisman__kart-bilgi');
    bilgi.append(olustur('span', 'eos-danisman__kart-ad', urun.title), olustur('span', 'eos-danisman__kart-fiyat', fiyat));
    baglanti.append(gorsel, bilgi);
    const oge = document.createElement('li');
    oge.append(baglanti);
    return oge;
  }

  /** One message row: bubble, text, timestamp. @param {string} metin @param {'rehber' | 'musteri'} kim */
  #balon(metin, kim) {
    const satir = olustur('div', `eos-danisman__satir eos-danisman__satir--${kim}`);
    const balon = olustur('div', 'eos-danisman__balon');
    balon.append(olustur('p', 'eos-danisman__metin', metin), olustur('span', 'eos-danisman__saat', damga()));
    satir.append(balon);
    this.refs.log.append(satir);
    this.#enAlta();
    return satir;
  }

  /** Newest message stays in view. */
  #enAlta() {
    this.refs.log.scrollTop = this.refs.log.scrollHeight;
  }

  /** Number of conversation rows after the Liquid-rendered intro, ignoring the transient indicator. */
  #sohbetUzunlugu() {
    const { log } = this.refs;
    return log.children.length - log.querySelectorAll(':scope > [data-giris], :scope > [data-gecici]').length;
  }

  /** Keeps the intro plus the first `adet` conversation rows. @param {number} adet */
  #kes(adet) {
    for (const gecici of this.refs.log.querySelectorAll(':scope > [data-gecici]')) gecici.remove();
    while (this.#sohbetUzunlugu() > adet && this.refs.log.lastElementChild) this.refs.log.lastElementChild.remove();
  }

  #kontrolleriGuncelle() {
    const cevapSayisi = this.#gecmis.length - (this.#sonucta ? 0 : 1);
    for (const dugme of [this.refs.geri, this.refs.bastan]) {
      // While the indicator runs the history is mid-move: hold the current visibility, just lock them.
      if (!this.#bekliyor) dugme.hidden = cevapSayisi < 1;
      dugme.setAttribute('aria-disabled', String(this.#bekliyor));
    }
  }

  /** Moves focus only when it is in the panel or was lost with a removed answer. @param {HTMLElement | null} hedef */
  #odakla(hedef) {
    const aktif = document.activeElement;
    if (!hedef || !(aktif === document.body || aktif === null || this.refs.panel.contains(aktif))) return;
    hedef.focus({ preventScroll: true });
  }
}

if (!customElements.get('eos-danisman-component')) {
  customElements.define('eos-danisman-component', EosDanisman);
}
